import React, { useState } from 'react';
import { ShieldCheck, AlertTriangle } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface Props {
  onAccept: () => void;
}

const STORAGE_KEY = 'mediward_disclaimer_accepted_v1';
const DOC_VERSION = '1.0';

export function hasAcceptedDisclaimer(): boolean {
  return localStorage.getItem(STORAGE_KEY) === 'true';
}

export function markDisclaimerAccepted(): void {
  localStorage.setItem(STORAGE_KEY, 'true');
}

/** Clears local acceptance flag — call on logout so the next user sees the disclaimer. */
export function clearDisclaimerAccepted(): void {
  localStorage.removeItem(STORAGE_KEY);
}

/**
 * Has the CURRENT signed-in user already accepted this disclaimer version, per the
 * server-side audit table? Lets a returning user skip the disclaimer across devices
 * without weakening shared-device compliance (a different user has no row yet).
 * Returns false on any error so we fail safe (show the disclaimer).
 */
export async function hasAcceptedDisclaimerRemote(): Promise<boolean> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return false;
    const { data, error } = await supabase
      .from('compliance_acceptances')
      .select('id')
      .eq('user_id', user.id)
      .eq('acceptance_type', 'clinical_disclaimer')
      .eq('doc_version', DOC_VERSION)
      .limit(1)
      .maybeSingle();
    if (error) return false;
    return !!data;
  } catch {
    return false;
  }
}

async function logAcceptanceToDB(): Promise<void> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    // Get hospital_id for this user
    const { data: appUser } = await supabase
      .from('app_users')
      .select('hospital_id')
      .eq('id', user.id)
      .maybeSingle();

    if (!appUser?.hospital_id) return;

    await supabase.from('compliance_acceptances').insert({
      user_id:         user.id,
      hospital_id:     appUser.hospital_id,
      acceptance_type: 'clinical_disclaimer',
      doc_version:     DOC_VERSION,
      user_agent:      navigator.userAgent.slice(0, 500),
    });
  } catch {
    // Non-blocking — localStorage already records acceptance
  }
}

const ClinicalDisclaimer: React.FC<Props> = ({ onAccept }) => {
  const [checked, setChecked]   = useState(false);
  const [loading, setLoading]   = useState(false);

  const handleAccept = async () => {
    setLoading(true);
    markDisclaimerAccepted();
    await logAcceptanceToDB();
    setLoading(false);
    onAccept();
  };

  return (
    <div className="fixed inset-0 z-[9998] flex items-center justify-center bg-black/60 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90svh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="px-6 py-5 bg-amber-50 border-b border-amber-200 flex items-start gap-3">
          <div className="bg-amber-100 p-2 rounded-lg shrink-0 mt-0.5">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
          </div>
          <div>
            <h2 className="font-bold text-slate-800 text-lg leading-tight">Clinical Decision Support — Disclaimer</h2>
            <p className="text-xs text-amber-700 mt-0.5">Please read before proceeding · v{DOC_VERSION}</p>
          </div>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 px-6 py-5 space-y-4 text-sm text-slate-700">
          <p>
            <strong>MediWard</strong> is a clinical workflow and documentation aid. It is designed to support — not
            replace — the professional judgement of licensed healthcare practitioners.
          </p>

          <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-800 text-xs space-y-1">
            <p className="font-semibold">This software does NOT:</p>
            <ul className="list-disc list-inside space-y-0.5 pl-1">
              <li>Provide medical diagnoses or treatment recommendations</li>
              <li>Replace consultation with a qualified clinician</li>
              <li>Guarantee accuracy of displayed clinical data</li>
              <li>Serve as a regulatory-approved medical device (unless explicitly certified)</li>
            </ul>
          </div>

          <p>
            All clinical decisions — including prescriptions, procedures, discharge, and emergency responses — remain
            the sole responsibility of the attending clinician.
          </p>

          <div className="space-y-2">
            <p className="font-semibold text-slate-800">Data &amp; Privacy</p>
            <p>
              Patient data is stored in a secure cloud database and is subject to applicable data protection laws
              (including the DPDP Act, 2023 for Indian deployments). Your institution is the data controller.
              Do not enter identifiable patient information unless your institution has authorised use of this system.
            </p>
            <p className="text-xs bg-blue-50 border border-blue-200 rounded-lg p-2.5 text-blue-800">
              <strong>Data Residency:</strong> All patient data is stored and processed exclusively in India
              (Supabase region: <code className="font-mono">ap-south-1</code> — Mumbai). No data leaves Indian
              jurisdiction. This meets DPDP Act, 2023 data localisation requirements.
            </p>
          </div>

          <div className="space-y-2">
            <p className="font-semibold text-slate-800">Limitation of Liability</p>
            <p>
              The developers and operators of MediWard shall not be liable for any clinical outcome, adverse event,
              or patient harm arising from reliance on information displayed within this application.
            </p>
          </div>

          <p className="text-xs text-slate-400">
            Your acceptance is logged with a timestamp for audit purposes as required by the DPDP Act, 2023.
            By continuing, you acknowledge that you are a licensed healthcare professional authorised by your
            institution to access this system.
          </p>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-100 bg-slate-50 space-y-3">
          <label className="flex items-start gap-2.5 cursor-pointer">
            <input
              type="checkbox"
              checked={checked}
              onChange={e => setChecked(e.target.checked)}
              className="mt-0.5 w-4 h-4 rounded accent-blue-600 shrink-0"
            />
            <span className="text-sm text-slate-700">
              I have read and understood this disclaimer. I am a licensed healthcare professional authorised
              to use this system.
            </span>
          </label>
          <button
            disabled={!checked || loading}
            onClick={handleAccept}
            className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl font-semibold text-sm transition-all
              disabled:bg-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed
              enabled:bg-teal-600 enabled:hover:bg-teal-700 enabled:text-white"
          >
            <ShieldCheck className="w-4 h-4" />
            {loading ? 'Recording acceptance…' : 'Accept & Continue'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ClinicalDisclaimer;
